import type { CatchErrorMetadata, LogType } from 'typings'

import {
  hasLoggedRequestError,
  isResponseLike,
  toJsonValue,
  toLoggableError,
} from '../loggableError'
import { isRequestFailure, toRequestLog } from '../requestError'

function readCatchContext(error: Error): {
  payload?: unknown
  cause?: unknown
} {
  const { cause } = error

  if (
    cause !== null &&
    typeof cause === 'object' &&
    !(cause instanceof Error) &&
    !isResponseLike(cause) &&
    ('payload' in cause || 'error' in cause)
  ) {
    const record = cause as { payload?: unknown; error?: unknown }

    return { payload: record.payload, cause: record.error }
  }

  if (cause !== undefined) return { cause }

  return {}
}

type AdminData = {
  account: string
  production: boolean
}

type SendLogProps = LogType & AdminData

const sendLog = (params: SendLogProps) => {
  const { level, type, metadata, message, account, production } = params
  fetch('/api/logger', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      level,
      metadata,
      type,
      account,
      production,
      message,
    }),
  })
}

type CatchLoggerType<T> = {
  /** Stable identifier for the catch site — always sent in metadata.id */
  id: string
  fn: () => T
  admin: AdminData
  onError?: () => void
  throwOnError?: boolean
}

export function catchLogger<T>(
  props: CatchLoggerType<T> & { throwOnError: true }
): T
export function catchLogger<T>(
  props: CatchLoggerType<T> & { throwOnError: false }
): T | undefined
export function catchLogger<T>(
  props: Omit<CatchLoggerType<T>, 'throwOnError'>
): T
export function catchLogger<T>(props: CatchLoggerType<T>): T | undefined {
  const {
    fn,
    admin: { account, production },
    onError = () => {},
    throwOnError = true,
    id,
  } = props

  const handleError = (err: unknown) => {
    onError()

    if (isRequestFailure(err)) {
      if (!hasLoggedRequestError(err)) {
        const requestLog = toRequestLog(err)

        sendLog({
          type: 'request',
          message: requestLog.message,
          level: requestLog.level,
          account,
          production,
          metadata: requestLog.metadata,
        })
      }

      if (throwOnError) {
        throw err instanceof Error
          ? err
          : new Error(toLoggableError(err).message)
      }

      return
    }

    const error =
      err instanceof Error ? err : new Error(toLoggableError(err).message)
    const { payload, cause } = readCatchContext(error)
    const metadata: CatchErrorMetadata = { id }
    const serializedPayload = toJsonValue(payload)
    const serializedCause =
      cause === undefined ? undefined : toLoggableError(cause)

    if (serializedPayload !== undefined) metadata.payload = serializedPayload
    if (serializedCause !== undefined) metadata.cause = serializedCause

    sendLog({
      type: 'catch',
      message: error.message,
      level: 'error',
      account,
      production,
      metadata,
    })

    if (throwOnError) throw error
  }

  try {
    const result = fn()

    if (result instanceof Promise) {
      return result.catch((err) => {
        handleError(err)
        return undefined
      }) as T
    }

    return result
  } catch (err) {
    handleError(err)
    return undefined
  }
}
